import React, { useRef, useState } from 'react'
import { Services } from '@/services/services';
import { CurrentSession } from '@/services/repository/session';
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout';
import { PageQuery, PageResponse, SearchTable, SearchTableParams, SearchTableRef } from '@/components/search/SearchTable';
import { useModal } from '@/components/modal/Modal';
import { formatDate } from '@/common/time';
import { isDesktopScreen, useResponsiveChange } from '@/common/device';
import { BackButton } from '@/components/layout/NavBar';
import { MainPageLayout } from '@/components/layout/MainLayout';
import { useTranslations } from '@/hooks/useTranslations';
import { useParams } from 'react-router-dom';

export interface DeviceHit {
  id: string;
  dt_tracker: string; // ISO Date string
  lat: number;
  lng: number;
  altitude: number;
  angle: number;
  speed: number;
  hitX?: number;
  hitY?: number;
  hitZ?: number;
  ucid?: string;
}

export interface HitSearchPageProps {
  services: Services,
  session: CurrentSession,
}

export const CommunalMachineHitSearchPage = ({ services, session }: HitSearchPageProps) => {
  const searchTableRef = useRef<SearchTableRef>(null)
  const translates = useTranslations(services, session)
  const machineId = useParams().mid
  const [error, setError] = useState<boolean>(false)

  useResponsiveChange()

  const tableParams: SearchTableParams<DeviceHit> = {
    fetch: (q: PageQuery): Promise<PageResponse<DeviceHit>> => {
      return services.session.fetchWithToken(
        `/api/communal/machines/byid/${machineId}/hits/search?pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`
      ).then((r) => {
        if (r.status === 200) {
          // Unwrap JSON: { pageSize, pageNumber, totalPages, data } with columns adapted for DeviceHit
          return r.json().then((data: any) => {
            // payload is likely: {pageSize, pageNumber, totalPages, data: Hit[]}
            // parse date strings
            return {
              ...data,
              data: (data.data || []).map((h: any) => ({
                ...h,
                dt_tracker: h.dt_tracker, // ISO string
                lat: +h.lat,
                lng: +h.lng,
                altitude: +h.altitude,
                angle: +h.angle,
                speed: +h.speed,
                hitX: h.hitX !== undefined ? +h.hitX : undefined,
                hitY: h.hitY !== undefined ? +h.hitY : undefined,
                hitZ: h.hitZ !== undefined ? +h.hitZ : undefined,
                ucid: h.ucid,
              })),
            };
          })
        } else {
          setError(true)
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    header: () => <>
      <div className='hcol'>{session.translates.for('elements.table.hits.createdAt')}</div>
      <div className='hcol'>{session.translates.for('elements.table.hits.ucid')}</div>
      <div className='hcol'>{session.translates.for('elements.table.hits.x')}</div>
      <div className='hcol'>{session.translates.for('elements.table.hits.y')}</div>
      {/* <div className='hcol'>{session.translates.for('elements.table.hits.z')}</div> */}
      <div className='hcol'>{session.translates.for('elements.table.hits.lat')}</div>
      <div className='hcol'>{session.translates.for('elements.table.hits.lng')}</div>
      <div className='hcol'>{session.translates.for('elements.table.hits.altitude')}</div>
      {/* <div className='hcol'>{session.translates.for('elements.table.hits.speed')}</div> */}
    </>,
    row: (row) => <>
      <div className='col'>{formatDate(new Date(row.dt_tracker))}</div>
      <div className='col'>{row.ucid || '-'}</div>
      <div className='col'>{row.hitX ? row.hitX.toFixed(2) + 'FV' : '-'}</div>
      <div className='col'>{row.hitY ? row.hitY.toFixed(2) + 'FV' : '-'}</div>
      {/* <div className='col'>{row.hitZ ? row.hitZ.toFixed(2) + 'FV' : '-'}</div> */}
      <div className='col'>{row.lat || '-'}</div>
      <div className='col'>{row.lng || '-'}</div>
      <div className='col'>{row.altitude || '-'}</div>
      {/* <div className='col'>{row.speed.toFixed(2)+ 'km/h'}</div> */}
    </>,
    translates: session.translates
  }

  if(translates===undefined) {
    return <div className='spinner'></div>
  }

  return (
    <MainPageLayout pageName='hits-search' services={services} session={session} translates={translates}
      navbarLeft={<BackButton services={services} translates={translates} />}>
      { error ? <div className='critic-error-box'>{translates.for('errors.general_error')}</div> : <SearchTable ref={searchTableRef} {...tableParams} /> }
    </MainPageLayout>
  )
}